/* Cart Drawer — talks exclusively to Shopify's native Ajax Cart API.
 * Shopify's cart is always the source of truth: every render below comes
 * straight from a server response, never from a locally-computed guess.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var ROUTES = {
    add: ROOT.replace(/\/$/, '') + '/cart/add.js',
    change: ROOT.replace(/\/$/, '') + '/cart/change.js',
    cart: ROOT.replace(/\/$/, '') + '/cart.js'
  };

  function formatAmount(cents, precision, thousands, decimal) {
    var parts = (cents / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  /* Money is always derived from the store — no currency symbol is ever hardcoded here.
   *  - Same currency as the shop: expand the merchant's own money format string, so this
   *    matches every other price on the page exactly. Change the currency or the format in
   *    Shopify and this follows automatically.
   *  - Different presentment currency (Shopify Markets — e.g. selling in USD from a EUR
   *    shop): format with Intl using the cart's actual currency, so the symbol is never wrong.
   */
  function fmtMoney(cents, currencyCode) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var assets = window.themeAssets || {};
    var shopCurrency = assets.shopCurrency;
    var active = currencyCode
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || shopCurrency;

    if (active && shopCurrency && active !== shopCurrency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: active
        }).format(cents / 100);
      } catch (e) { /* fall through to the merchant's money format */ }
    }

    var format = assets.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount': return formatAmount(cents, 2, ',', '.');
        case 'amount_no_decimals': return formatAmount(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return formatAmount(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatAmount(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatAmount(cents, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator': return formatAmount(cents, 0, "'", '.');
        case 'amount_with_space_separator': return formatAmount(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return formatAmount(cents, 0, ' ', ',');
        case 'amount_with_period_and_space_separator': return formatAmount(cents, 2, ' ', '.');
        default: return formatAmount(cents, 2, ',', '.');
      }
    });
  }

  function track(name, detail) {
    if (typeof window.trackEvent === 'function') window.trackEvent(name, detail);
  }

  function getDrawer() { return document.getElementById('CartDrawer'); }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    track('cart_opened');
    var closeBtn = drawer.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
  }

  function replaceDrawerHtml(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newDrawer = doc.getElementById('CartDrawer');
    var oldDrawer = getDrawer();
    if (!newDrawer || !oldDrawer) return;
    var wasOpen = oldDrawer.classList.contains('is-open');
    oldDrawer.replaceWith(newDrawer);
    if (wasOpen) {
      newDrawer.classList.add('is-open');
      newDrawer.setAttribute('aria-hidden', 'false');
    }
    initDrawerEvents();
    if (wasOpen) refreshShippingBar();
  }

  /* Section Rendering API.
   * IMPORTANT: `?sections=` only works on a real storefront URL (/, /cart, /products/...).
   * `/cart.js?sections=` is NOT supported — it just returns the cart JSON with no section
   * payload, which is why the drawer used to open still showing its (empty) page-load HTML.
   */
  function sectionsBaseUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var path = window.location.pathname || root;
    if (/\.js$/.test(path)) path = root;
    return path;
  }

  function renderDrawerSection(sections) {
    if (!sections) return false;
    var html = sections[SECTION_ID];
    if (!html) return false;
    replaceDrawerHtml(html);
    return true;
  }

  function fetchCartSections() {
    var base = sectionsBaseUrl();
    return fetch(base + (base.indexOf('?') > -1 ? '&' : '?') + 'sections=' + SECTION_ID, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (renderDrawerSection(data)) return data;
        // Fallback: section_id returns the section as raw HTML.
        return fetch(base + '?section_id=' + SECTION_ID, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) { replaceDrawerHtml(html); return data; });
      })
      .catch(function (err) { console.error('[cart] drawer refresh failed', err); });
  }

  function afterCartChange(cart, sections) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    if (renderDrawerSection(sections)) {
      refreshShippingBar();
      return Promise.resolve(cart);
    }
    return fetchCartSections();
  }

  function handleError(err) {
    console.error('[cart]', err);
    var message = (err && err.description) || (err && err.message) || document.documentElement.dataset.cartErrorGeneric || 'Something went wrong. Please try again.';
    if (window.showToast) window.showToast(message, { error: true });
  }

  window.CartAPI = {
    addItem: function (variantId, quantity, opts) {
      opts = opts || {};
      var id = parseInt(variantId, 10);
      if (!id) {
        var noVariant = { description: 'No variant selected' };
        handleError(noVariant);
        return Promise.reject(noVariant);
      }
      return fetch(ROUTES.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: id, quantity: quantity || 1 }],
          sections: SECTION_ID,
          sections_url: sectionsBaseUrl()
        })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) { handleError(result.data); return Promise.reject(result.data); }
          var sections = result.data && result.data.sections;
          return fetch(ROUTES.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              return Promise.resolve(afterCartChange(cart, sections)).then(function () {
                if (!opts.silent) openDrawer();
                track('add_to_cart', { variant_id: id, quantity: quantity || 1 });
                return cart;
              });
            });
        })
        .catch(function (err) { handleError(err); throw err; });
    },

    changeLine: function (line, quantity) {
      return fetch(ROUTES.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION_ID, sections_url: sectionsBaseUrl() })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) { handleError(result.data); return Promise.reject(result.data); }
          return Promise.resolve(afterCartChange(result.data, result.data.sections)).then(function () { return result.data; });
        })
        .catch(function (err) { handleError(err); throw err; });
    },

    changeByKey: function (key, quantity) {
      return fetch(ROUTES.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID, sections_url: sectionsBaseUrl() })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) { handleError(result.data); return Promise.reject(result.data); }
          return Promise.resolve(afterCartChange(result.data, result.data.sections)).then(function () { return result.data; });
        })
        .catch(function (err) { handleError(err); throw err; });
    },

    upgradeBundle: function (fromKey, toVariantId) {
      return this.changeByKey(fromKey, 0).then(function () {
        return window.CartAPI.addItem(toVariantId, 1, { silent: true });
      }).then(function (cart) {
        openDrawer();
        track('cart_upsell_clicked', { variant_id: toVariantId });
        return cart;
      });
    },

    open: openDrawer,
    close: closeDrawer,
    refresh: fetchCartSections
  };

  function refreshShippingBar() {
    var bar = document.querySelector('[data-shipping-bar]');
    if (!bar) return;
    var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    fetch(ROUTES.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      var fill = bar.querySelector('[data-shipping-fill]');
      var msg = bar.querySelector('[data-shipping-message]');
      if (!threshold) return;
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (cart.total_price >= threshold) {
          msg.textContent = document.documentElement.dataset.freeShippingUnlocked || "You've unlocked FREE SHIPPING";
        } else {
          // cart.currency is the presentment currency Shopify actually charges in.
          var remaining = fmtMoney(threshold - cart.total_price, cart.currency);
          msg.textContent = "You're " + remaining + ' away from FREE SHIPPING';
        }
      }
    });
  }

  function updateUpsell() {
    var drawer = getDrawer();
    var upsell = document.querySelector('[data-cart-upsell]');
    if (!drawer || !upsell) return;

    var v1 = drawer.getAttribute('data-variant-1-id');
    var v2 = drawer.getAttribute('data-variant-2-id');
    var v3 = drawer.getAttribute('data-variant-3-id');
    var p1 = parseFloat(drawer.getAttribute('data-variant-1-price')) || 0;
    var p2 = parseFloat(drawer.getAttribute('data-variant-2-price')) || 0;
    var p3 = parseFloat(drawer.getAttribute('data-variant-3-price')) || 0;

    var lines = Array.prototype.slice.call(drawer.querySelectorAll('[data-cart-line-key]'));
    var currentLine = null;
    var target = null;

    lines.some(function (el) {
      var titleEl = el.querySelector('.cart-drawer__item-variant');
      var title = titleEl ? titleEl.textContent.trim() : '';
      if (title === '1 Pack' && v2) { currentLine = el; target = { id: v2, label: '2 Pack', savings: (p1 * 2 - p2) }; return true; }
      if (title === '2 Pack' && v3) { currentLine = el; target = { id: v3, label: '3 Pack', savings: (p1 * 3 - p3) }; return true; }
      return false;
    });

    if (!currentLine || !target) { upsell.hidden = true; return; }

    upsell.hidden = false;
    // target.savings is already in cents (variant prices come from Liquid in cents) — do not scale it again.
    var copyEl = upsell.querySelector('[data-upsell-copy]');
    var labelEl = upsell.querySelector('[data-upsell-cta-label]');
    if (copyEl) copyEl.textContent = 'Get the ' + target.label + ' and save an extra ' + fmtMoney(Math.round(target.savings)) + '.';
    if (labelEl) labelEl.textContent = 'Upgrade to ' + target.label;

    var btn = upsell.querySelector('[data-cart-upgrade]');
    if (!btn) return;
    btn.onclick = function () {
      btn.classList.add('btn-loading');
      window.CartAPI.upgradeBundle(currentLine.getAttribute('data-cart-line-key'), target.id)
        .finally(function () { btn.classList.remove('btn-loading'); });
    };
  }

  function initDrawerEvents() {
    var drawer = getDrawer();
    if (!drawer) return;

    // Guard: this runs on DOMContentLoaded, on shopify:section:load and after every
    // re-render. Binding twice on the same DOM made qty +/- fire two requests per click.
    if (drawer.dataset.drawerInit) {
      updateUpsell();
      refreshShippingBar();
      return;
    }
    drawer.dataset.drawerInit = 'true';

    drawer.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });

    drawer.querySelectorAll('[data-cart-line-key]').forEach(function (item) {
      var key = item.getAttribute('data-cart-line-key');
      var qtyValue = item.querySelector('[data-qty-value]');
      var dec = item.querySelector('[data-qty-decrease]');
      var inc = item.querySelector('[data-qty-increase]');
      var removeBtn = item.querySelector('[data-cart-remove]');

      if (dec) dec.addEventListener('click', function () {
        var qty = Math.max(0, parseInt(qtyValue.textContent, 10) - 1);
        item.classList.add('is-updating');
        window.CartAPI.changeByKey(key, qty).finally(function () { item.classList.remove('is-updating'); });
      });
      if (inc) inc.addEventListener('click', function () {
        var qty = parseInt(qtyValue.textContent, 10) + 1;
        item.classList.add('is-updating');
        window.CartAPI.changeByKey(key, qty).finally(function () { item.classList.remove('is-updating'); });
      });
      if (removeBtn) removeBtn.addEventListener('click', function () {
        item.classList.add('is-updating');
        window.CartAPI.changeByKey(key, 0);
      });
    });

    updateUpsell();
    refreshShippingBar();
  }

  /* Checkout must never be swallowed.
   * Registered in the CAPTURE phase on document, so it runs before any other click
   * handler on the page — including anything that calls stopPropagation or
   * preventDefault — and it navigates explicitly instead of relying on the element's
   * own default action. That makes it work whether the control is a link, a plain
   * button, or a form submit, and regardless of what else is bound to the page.
   */
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-cart-checkout]') : null;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    var url = el.getAttribute('href') || '';
    if (url.charAt(0) !== '/') url = '/checkout';
    window.location.assign(url);
  }, true);

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var drawer = getDrawer();
      if (drawer && drawer.classList.contains('is-open')) closeDrawer();
    }
  });

  document.addEventListener('DOMContentLoaded', initDrawerEvents);
  document.addEventListener('shopify:section:load', initDrawerEvents);

  window.fmtMoney = fmtMoney;
})();
